/// <amd-module name="Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService" />

import * as React from "react";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { INavigationStateChangedEvent, IVssHistoryService } from "VSS/Platform/History";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

type SaveCompleteListener = ((pageContext: IVssPageContext, workItem: IWorkItem, includeHistory?: boolean) => void) | undefined;

export interface IWorkItemFormDialogService extends IVssService {
    /**
     * Show a work item form dialog for the given work item id.
     * Additionally adds a workitem parameter to the query string and a browser history entry.
     * @param updateHistory Should history be updated and the url changed. Defualt is true
     */
    showWorkItemFormDialog(
        itemOrItemId: number | IWorkItem,
        onSaveComplete: SaveCompleteListener,
        options?: {
            onDelete?: () => void;
            onDismiss?: () => void;
            shouldRefresh?: boolean;
            updateHistory?: boolean;
        }
    ): void;
    /**
     * Listen to the history service to open/close the work item form dialog as appropriate. If there is already a
     * workitem parameter in the query string, open a dialog for that work item.
     */
    subscribeToHistoryService(saveCompleteListener: SaveCompleteListener): void;
}

class WorkItemFormDialogService extends VssService implements IWorkItemFormDialogService {
    private historyService: IVssHistoryService;
    /** current work item displayed in the form dialog */
    private currentWorkItemFormItemId: number | undefined;
    private dismissWorkItemForm: ((updateHistory: boolean) => void) | undefined;
    /** this is the save complete listener used when opening dialogs in response to page navigation */
    private saveCompleteListener: SaveCompleteListener;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this.historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);
        this.historyService.unsubscribe(this.historyServiceListener);
    }

    public subscribeToHistoryService(saveCompleteListener: SaveCompleteListener) {
        this.historyService.unsubscribe(this.historyServiceListener);
        this.saveCompleteListener = saveCompleteListener;

        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();

        if (historyState["workitem"]) {
            const workItemId = Number(historyState["workitem"]);
            if (!isNaN(workItemId)) {
                this.showWorkItemFormDialog(workItemId, this.saveCompleteListener, { updateHistory: false });
            }
        }

        this.historyService.subscribe(this.historyServiceListener);
    }

    public showWorkItemFormDialog(
        itemOrItemId: number | IWorkItem,
        onSaveComplete: SaveCompleteListener,
        options?: {
            onDelete?: () => void;
            onDismiss?: () => void;
            shouldRefresh?: boolean;
            updateHistory?: boolean;
        }
    ) {
        let item: IWorkItem | undefined;
        let itemId: number;
        if (typeof itemOrItemId === "number") {
            itemId = itemOrItemId;
        } else {
            item = itemOrItemId;
            itemId = item.id;
        }
        const layoutService = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        if (options?.updateHistory) {
            const currentState = this.historyService.getState();
            if (currentState["workitem"] !== "" + itemId) {
                this.historyService.pushState({ ...currentState, workitem: "" + itemId });
            }
        }
        this.currentWorkItemFormItemId = itemId;
        layoutService.renderCallout(onDismiss => {
            const localDismiss = (updateHistory = true) => {
                this.currentWorkItemFormItemId = undefined;
                this.dismissWorkItemForm = undefined;
                if (updateHistory) {
                    const currentState = this.historyService.getState();
                    delete currentState["workitem"];
                    this.historyService.pushState(currentState);
                }
                options?.onDismiss?.();
                onDismiss();
            };
            this.dismissWorkItemForm = localDismiss;
            const commonProps = {
                onDelete: options?.onDelete,
                onDismiss: localDismiss,
                shouldRefresh: options?.shouldRefresh,
                onSaveComplete: (workItem: IWorkItem, includeHistory?: boolean) => onSaveComplete?.(this.pageContext, workItem, includeHistory)
            };
            if (item) {
                return <WorkItemFormDialog {...commonProps} workItem={item} />;
            } else {
                return <WorkItemFormDialog {...commonProps} workItemId={itemId} />;
            }
        });
    }

    private historyServiceListener = (event: INavigationStateChangedEvent) => {
        const workItemId = Number(event.newState?.state["workitem"]);
        if (workItemId !== this.currentWorkItemFormItemId) {
            if (this.currentWorkItemFormItemId) {
                this.dismissWorkItemForm?.(/* updateHistory */ false);
            }
            if (!isNaN(workItemId)) {
                this.showWorkItemFormDialog(workItemId, this.saveCompleteListener, { updateHistory: false });
            }
        }
    };
}

Services.add("IWorkItemFormDialogService", { serviceFactory: WorkItemFormDialogService });
